import { InjectionToken, Signal } from '@angular/core';
import { Observable } from 'rxjs';
import { AuthUserInfo } from './auth';

/**
 * UserValue can be:
 * - AuthUserInfo when the user is logged in
 * - undefined when the user info is not yet loaded
 * - null when the user value existed and the user is not authenticated anymore
 */
export type UserValue = AuthUserInfo | undefined | null;

export interface IUserService {
  user$: Observable<UserValue>;
  $user: Signal<UserValue>;
  /**
   * Checks if current loggedIn User has at least one of the roles
   * @param roles
   */
  hasOneRole(roles: string[] | undefined): boolean;
  /**
   * Check if current loggedIn User has a specific role
   * @param role
   */
  hasRole(role: string): boolean;
  /**
   * Check if current loggedIn User has all Roles
   * @param roles
   */
  hasAllRoles(roles: string[]): boolean;
  /**
   * Check if user has a role with provided appId
   * @param appId
   */
  hasApplication(appId: string): boolean;
  /**
   * Check if user is one of partner types
   * @param partnerTypes
   */
  isOneOfPartnerTypes(partnerTypes: number[]): boolean;
  /**
   * Check if a user is partner type
   * @param partnerType
   */
  isPartnerType(partnerType: number): boolean;
  /**
   * Check if a user is partner type
   * @param partnerType
   */
  isPartnerType$(partnerType: number): Observable<boolean>;

  /**
   * Check if user is not partner type
   */
  hasNoPartnerType(): boolean;
  /**
   * Check if user is in a specific user group
   * @param userGroup
   */
  isInUserGroup(userGroup: string): boolean;
  /**
   * Check if user is in a specific user group (Observable version)
   * @param userGroup
   */
  isInUserGroup$(userGroup: string): Observable<boolean>;
  /**
   * Check if user is in one of the user groups
   * @param userGroups
   */
  isInOneOfUserGroups(userGroups: string[]): boolean;
  /**
   * Check if user is in one of the user groups (Observable version)
   * @param userGroups
   */
  isInOneOfUserGroups$(userGroups: string[]): Observable<boolean>;
  /**
   * Checks if current logged-in User has one of the roles (Observable version)
   * @param roles
   */
  hasOneRole$(roles: string[] | undefined): Observable<boolean>;
  /**
   * Checks if current logged-in User has a specific role (Observable version)
   * @param role
   */
  hasRole$(role: string): Observable<boolean>;
  /**
   * Checks if current logged-in User has all roles (Observable version)
   * @param roles
   */
  hasAllRoles$(roles: string[]): Observable<boolean>;
  /**
   * Checks if user has a role with the provided appId (Observable version)
   * @param appId
   */
  hasApplication$(appId: string): Observable<boolean>;
  /**
   * Checks if user is one of the partner types (Observable version)
   * @param partnerTypes
   */
  isOneOfPartnerTypes$(partnerTypes: number[]): Observable<boolean>;
  /**
   * Checks if user's partner type is PALFINGER GmbH
   * @returns boolean
   */
  isAG(): boolean;
  /**
   * Checks if user's partner type is PALFINGER GmbH
   * @returns Observable<boolean>
   */
  isAG$(): Observable<boolean>;
  /**
   * Checks if user's partner type is Area
   * @returns boolean
   */
  isArea(): boolean;
  /**
   * Checks if user's partner type is Area
   * @returns Observable<boolean>
   */
  isArea$(): Observable<boolean>;
  /**
   * Checks if user's partner type is Service Partner
   * @returns boolean
   */
  isServicePartner(): boolean;
  /**
   * Checks if user's partner type is Service Partner
   * @returns Observable<boolean>
   */
  isServicePartner$(): Observable<boolean>;
  /** Checks if user's partner type is End Customer
   * @returns boolean
   */
  isEndCustomer(): boolean;
  /** Checks if user's partner type is End Customer
   * @returns Observable<boolean>
   */
  isEndCustomer$(): Observable<boolean>;
  /** Checks if user's partner type is Supplier
   * @returns boolean
   */
  isSupplier(): boolean;
  /** Checks if user's partner type is Supplier
   * @returns Observable<boolean>
   */
  isSupplier$(): Observable<boolean>;
  /**
   * Check if user's company area is PALFINGER GmbH
   */
  isGermany(): boolean;
  /**
   * Checks if user's company area is PALFINGER GmbH (Observable version)
   */
  isGermany$(): Observable<boolean>;
  /**
   * Returns the language code of the user or 'EN' if not set
   * @returns string
   */
  language(): string;
  /**
   * Uploads the user's signature to the identity service. Exposed here so UI
   * consumers depend only on the `USER_SERVICE` token instead of the generated
   * identity client directly — the concrete impl resolves in the app's root
   * injector (where the client is configured), avoiding the module-federation
   * "multiple unconfigured instances" problem in shared/remote-loaded libs.
   *
   * After the upload the service refreshes the user info from identity (the
   * single source of truth) so the immutable `$user()` reflects the new
   * signature — callers do not refresh manually.
   * @param signature the signature image blob
   */
  uploadSignature(signature: Blob): Observable<void>;
}

export const USER_SERVICE = new InjectionToken<IUserService>('USER_SERVICE', {
  providedIn: 'root',
  factory: () => {
    throw new Error(
      'No UserService implementation provided. Please provide one using UserModule.forHost() or UserModule.forRemote()',
    );
  },
});
